import { Link } from 'react-router-dom';
import { ArrowUpRight, X, UserX } from 'lucide-react';
import { SmartPatientInfoSections } from '../smart/SmartPatientInfoDrawer';
import { ClinicianTwinSummary } from '../digital-twin/ClinicianTwinSummary';
import { useSmartPatientContext } from '../../hooks/useSmartPatientContext';
import { clearPatientConnectionState } from '../../api/smartApi';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';

interface PatientSheetProps {
  open: boolean;
  onClose: () => void;
  patientName?: string;
  patientMrn?: string;
}

/**
 * Everything about the CURRENT patient behind one affordance: clicking the
 * patient chip in the top bar opens this right-hand sheet with the digital
 * twin at a glance, the full connected record beneath it, and the
 * wrong-patient escape at the bottom. Replaces the two separate rail drawers
 * (Patient info + Digital Twin) — a doctor thinks "show me the patient",
 * not "which of two panels holds the twin?".
 */
/**
 * The connected-record body fetches on mount, so it lives inside SheetContent:
 * Radix only mounts content while the sheet is open, which keeps the fetch
 * on-open (not on app load) now that the sheet root itself stays mounted.
 */
function PatientSheetBody({ patientMrn }: { patientMrn?: string }) {
  // The ONLY opt-in to the clinical preview in the app: `SmartPatientInfoDrawer`
  // below is the only component that reads `context.preview`. The call is a full
  // chart re-import (see useSmartPatientContext), so every other consumer of the
  // hook deliberately goes without it. Radix mounts this body only while the
  // sheet is open, so the import stays tied to an explicit clinician action.
  const context = useSmartPatientContext({ withPreview: true });

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
      {patientMrn?.trim() ? (
        <section className="mb-4">
          <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.18em] text-text-tertiary">
            Digital twin
          </div>
          <ClinicianTwinSummary patientId={patientMrn} />
          <Link
            to={`/patient/${encodeURIComponent(patientMrn)}/digital-twin`}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
          >
            View full digital twin <ArrowUpRight className="h-3 w-3" aria-hidden />
          </Link>
        </section>
      ) : null}
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.18em] text-text-tertiary">
        Connected record
      </div>
      <SmartPatientInfoSections context={context} />
    </div>
  );
}

export function PatientSheet({ open, onClose, patientName, patientMrn }: PatientSheetProps) {
  const disconnectPatient = () => {
    clearPatientConnectionState();
    window.location.reload();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-[min(94vw,28rem)] gap-0 border-border sm:max-w-none"
        {...(patientMrn ? {} : { 'aria-describedby': undefined })}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <SheetTitle className="truncate text-base font-medium text-foreground">
              {patientName?.trim() || 'Patient'}
            </SheetTitle>
            {patientMrn ? (
              <SheetDescription className="truncate font-mono text-xs text-text-tertiary">
                MRN {patientMrn}
              </SheetDescription>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-text-tertiary transition-colors hover:bg-surface-hover hover:text-foreground"
            aria-label="Close patient overview"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <PatientSheetBody patientMrn={patientMrn} />

        <div className="shrink-0 border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={disconnectPatient}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-destructive/40 px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            <UserX className="h-3.5 w-3.5" aria-hidden />
            Not this patient — disconnect
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
